import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MemberAvatar } from '@/components/MemberAvatar';
import NotificationCenter from '@/components/NotificationCenter';
import GlobalSearch from '@/components/GlobalSearch';
import { Logo } from '@/components/Logo';
import { ThemeQuickToggle } from '@/components/ThemeQuickToggle';
import {
  Users, LayoutDashboard, FileText, CreditCard, CalendarDays, Settings,
  LogOut, ChevronDown, Menu, X, BarChart3, MessageSquare, FolderOpen, Zap,
  Mail, Webhook, HelpCircle, MessageCircle, ClipboardList, Shield, Gift,
  ShieldCheck, Trophy, Search, Inbox, Tag, Upload, Megaphone, Compass,
} from 'lucide-react';

const ALL_ROLES = ['super_admin', 'admin', 'member', 'viewer'];
const ROLES_NO_VIEWER = ['super_admin', 'admin', 'member'];
const ADMIN_ROLES = ['super_admin', 'admin'];
const SUPER_ONLY = ['super_admin'];

// Each group: { id, label, icon, items: [...], roles (union of children) }
// Items: { path, label, icon, roles }
const NAV_GROUPS = [
  {
    id: 'leads', label: 'Leads', icon: FileText, roles: ALL_ROLES,
    items: [
      { path: '/leads', label: 'All Leads', icon: FileText, roles: ALL_ROLES },
      { path: '/find-leads', label: 'Find Leads', icon: Search, roles: SUPER_ONLY },
      { path: '/lead-requests', label: 'Lead Requests', icon: Inbox, roles: ROLES_NO_VIEWER },
      { path: '/import/crm', label: 'Import CRM', icon: Upload, roles: ROLES_NO_VIEWER },
    ],
  },
  {
    id: 'outreach', label: 'Outreach', icon: Megaphone, roles: ALL_ROLES,
    items: [
      { path: '/appointments', label: 'Calendar', icon: CalendarDays, roles: ALL_ROLES },
      { path: '/messages', label: 'Messages', icon: MessageSquare, roles: ROLES_NO_VIEWER },
      { path: '/sms-inbox', label: 'SMS Inbox', icon: MessageSquare, roles: ROLES_NO_VIEWER },
      { path: '/email', label: 'Email', icon: Mail, roles: ROLES_NO_VIEWER },
      { path: '/admin/campaigns', label: 'Campaigns', icon: Mail, roles: ADMIN_ROLES },
      { path: '/groups', label: 'Groups', icon: FolderOpen, roles: ROLES_NO_VIEWER },
      { path: '/groups/discover', label: 'Discover Groups', icon: Compass, roles: ROLES_NO_VIEWER },
    ],
  },
  {
    id: 'insights', label: 'Insights', icon: BarChart3, roles: ALL_ROLES,
    items: [
      { path: '/reports', label: 'Reports', icon: ClipboardList, roles: ALL_ROLES },
      { path: '/analytics', label: 'My Analytics', icon: BarChart3, roles: ALL_ROLES },
      { path: '/admin/analytics', label: 'Team Analytics', icon: BarChart3, roles: SUPER_ONLY },
      { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, roles: ALL_ROLES },
    ],
  },
  {
    id: 'team', label: 'Team', icon: Users, roles: ROLES_NO_VIEWER,
    items: [
      { path: '/members', label: 'Members', icon: Users, roles: ROLES_NO_VIEWER },
      { path: '/admin/free-accounts', label: 'Free Accounts', icon: Gift, roles: SUPER_ONLY },
      { path: '/admin/audit-log', label: 'Audit Log', icon: Shield, roles: SUPER_ONLY },
    ],
  },
  {
    id: 'admin', label: 'Admin', icon: Settings, roles: ADMIN_ROLES,
    items: [
      { path: '/admin/assignment-rules', label: 'Assignment Rules', icon: Zap, roles: ADMIN_ROLES },
      { path: '/admin/webhooks', label: 'Webhooks', icon: Webhook, roles: ADMIN_ROLES },
      { path: '/admin/coupons', label: 'Coupons', icon: Tag, roles: ADMIN_ROLES },
      { path: '/admin/sms-campaign', label: 'SMS Campaign', icon: MessageCircle, roles: SUPER_ONLY },
      { path: '/admin/sms-consent-log', label: 'SMS Consent Log', icon: ShieldCheck, roles: SUPER_ONLY },
      { path: '/admin/sms-drip', label: 'SMS Drip', icon: MessageCircle, roles: SUPER_ONLY },
      { path: '/admin/marketing-email', label: 'Marketing Email', icon: Mail, roles: SUPER_ONLY },
      { path: '/support', label: 'Support Inbox', icon: MessageCircle, roles: SUPER_ONLY },
      { path: '/admin/code-quality', label: 'Code Quality', icon: ShieldCheck, roles: SUPER_ONLY },
    ],
  },
];

const MODULE_KEY_MAP = {
  '/dashboard': 'dashboard', '/leads': 'leads', '/appointments': 'calendar',
  '/messages': 'messages', '/email': 'email', '/groups': 'groups',
  '/members': 'members', '/reports': 'reports', '/analytics': 'analytics',
  '/leaderboard': 'leaderboard', '/find-leads': 'find_leads',
  '/admin/analytics': 'analytics', '/admin/campaigns': 'campaigns',
  '/admin/assignment-rules': 'rules', '/admin/webhooks': 'webhooks',
  '/support': 'support', '/billing': 'billing',
  '/admin/audit-log': 'audit_log', '/admin/free-accounts': 'free_accounts',
  '/admin/code-quality': 'code_quality', '/admin/coupons': 'coupons',
  '/admin/sms-campaign': 'sms_campaign',
  '/admin/sms-consent-log': 'sms_consent_log',
  '/admin/sms-drip': 'sms_campaign',
  '/admin/marketing-email': 'marketing_email',
};

function canSee(item, user, userModules) {
  if (!item.roles.includes(user.role)) return false;
  if (user.role === 'super_admin') return true;
  if (!userModules) return true;
  const key = MODULE_KEY_MAP[item.path];
  return key ? userModules.includes(key) : true;
}

function filterGroups(user) {
  const userModules = user.module_permissions || null;
  return NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => canSee(i, user, userModules)) }))
    .filter(g => g.items.length > 0);
}

function isItemActive(pathname, itemPath) {
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

function isGroupActive(pathname, group) {
  return group.items.some(i => isItemActive(pathname, i.path));
}

function NavGroupDropdown({ group, pathname }) {
  const active = isGroupActive(pathname, group);
  const Icon = group.icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`nav-group-${group.id}`}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-sm transition-all duration-200 outline-none ${
          active ? 'text-[#0047AB] bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
        }`}
      >
        <Icon className="w-4 h-4" />
        {group.label}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {group.items.map(item => {
          const ItemIcon = item.icon;
          const itemActive = isItemActive(pathname, item.path);
          return (
            <DropdownMenuItem key={item.path} asChild data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Link
                to={item.path}
                className={`flex items-center gap-2 ${itemActive ? 'text-[#0047AB] bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' : ''}`}
              >
                <ItemIcon className="w-4 h-4" />
                {item.label}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileGroupSection({ group, pathname, onNavigate }) {
  const Icon = group.icon;
  return (
    <div className="mb-2" data-testid={`mobile-group-${group.id}`}>
      <div className="flex items-center gap-2 px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 font-semibold">
        <Icon className="w-3 h-3" /> {group.label}
      </div>
      {group.items.map(item => {
        const ItemIcon = item.icon;
        const active = isItemActive(pathname, item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
              active ? 'text-[#0047AB] bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ItemIcon className="w-4 h-4" />
            {item.label}
          </Link>
        );
      })}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (!user) return null;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const visibleGroups = filterGroups(user);
  const dashboardActive = location.pathname === '/dashboard';

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-8">
              <Link to="/dashboard" className="flex items-center" data-testid="navbar-logo">
                <Logo size="sm" showText={true} className="hidden sm:flex" />
                <Logo size="sm" showText={false} className="sm:hidden" />
              </Link>
              <div className="hidden lg:flex items-center gap-1">
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-sm transition-all duration-200 ${
                    dashboardActive ? 'text-[#0047AB] bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                {visibleGroups.map(g => (
                  <NavGroupDropdown key={g.id} group={g} pathname={location.pathname} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <GlobalSearch />
              <ThemeQuickToggle />
              <NotificationCenter />
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-2 py-1 text-sm rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200 outline-none" data-testid="user-menu-trigger">
                  <MemberAvatar userId={user?.id || user?._id} name={user?.name} size="sm" />
                  <span className="hidden sm:block text-slate-700 dark:text-slate-200 font-medium max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-sm">{user.role}</span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="menu-billing">
                    <CreditCard className="w-4 h-4 mr-2" /> Billing
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/help')} data-testid="menu-help">
                    <HelpCircle className="w-4 h-4 mr-2" /> Help & Manual
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                className="lg:hidden p-2 -mr-1 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle navigation menu"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <div className="fixed top-14 right-0 z-50 w-72 max-h-[calc(100vh-3.5rem)] overflow-y-auto bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-xl lg:hidden animate-in slide-in-from-right duration-200" data-testid="mobile-menu-panel" role="dialog" aria-label="Mobile navigation drawer">
            <div className="p-3">
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors mb-1 ${
                  dashboardActive ? 'text-[#0047AB] bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              {visibleGroups.map(g => (
                <MobileGroupSection
                  key={g.id} group={g} pathname={location.pathname}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
            </div>
            <div className="border-t border-slate-200 dark:border-slate-700 p-3">
              <Link
                to="/settings"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 active:bg-slate-100"
              >
                <Settings className="w-4 h-4" /> Settings
              </Link>
              <Link
                to="/help"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 active:bg-slate-100"
              >
                <HelpCircle className="w-4 h-4" /> Help & Manual
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/40 active:bg-red-100 w-full text-left"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
